// Inspired by react-hot-toast library
import { useState, useEffect } from 'react';

const TOAST_LIMIT = 20;
const TOAST_REMOVE_DELAY = 3000; // 3 seconds (default)

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
} as const;

interface Toast {
  id: string;
  open: boolean;
  duration?: number;
  [key: string]: unknown;
}

interface ToastState {
  toasts: Toast[];
}

interface AddToastAction {
  type: typeof actionTypes.ADD_TOAST;
  toast: Toast;
}

interface UpdateToastAction {
  type: typeof actionTypes.UPDATE_TOAST;
  toast: Partial<Toast> & { id: string };
}

interface DismissToastAction {
  type: typeof actionTypes.DISMISS_TOAST;
  toastId?: string;
}

interface RemoveToastAction {
  type: typeof actionTypes.REMOVE_TOAST;
  toastId?: string;
}

type ToastAction = AddToastAction | UpdateToastAction | DismissToastAction | RemoveToastAction;

let count = 0;

function genId(): string {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const toastDismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string, duration = TOAST_REMOVE_DELAY): void => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: actionTypes.REMOVE_TOAST,
      toastId,
    });
  }, duration);

  toastTimeouts.set(toastId, timeout);
};

const addToDismissQueue = (toastId: string, duration = TOAST_REMOVE_DELAY): void => {
  if (toastDismissTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastDismissTimeouts.delete(toastId);
    dispatch({
      type: actionTypes.DISMISS_TOAST,
      toastId,
    });
  }, duration);

  toastDismissTimeouts.set(toastId, timeout);
};

// Helper functions for reducer cases
const handleAddToast = (state: ToastState, action: AddToastAction): ToastState => {
  const toastDuration = action.toast.duration ?? TOAST_REMOVE_DELAY;
  if (toastDuration > 0) {
    addToDismissQueue(action.toast.id, toastDuration);
  }
  return {
    ...state,
    toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
  };
};

const handleUpdateToast = (state: ToastState, action: UpdateToastAction): ToastState => {
  return {
    ...state,
    toasts: state.toasts.map(t => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
  };
};

const clearToastTimeouts = (toastId?: string): void => {
  if (toastId) {
    if (toastTimeouts.has(toastId)) {
      clearTimeout(toastTimeouts.get(toastId));
      toastTimeouts.delete(toastId);
    }
    if (toastDismissTimeouts.has(toastId)) {
      clearTimeout(toastDismissTimeouts.get(toastId));
      toastDismissTimeouts.delete(toastId);
    }
  } else {
    // Clear all timeouts
    for (const timeout of toastTimeouts.values()) clearTimeout(timeout);
    for (const timeout of toastDismissTimeouts.values()) clearTimeout(timeout);
    toastTimeouts.clear();
    toastDismissTimeouts.clear();
  }
};

const scheduleRemoveToasts = (state: ToastState, toastId?: string): void => {
  if (toastId) {
    const toast = state.toasts.find(t => t.id === toastId);
    const removeDelay = toast?.duration ?? TOAST_REMOVE_DELAY;
    addToRemoveQueue(toastId, removeDelay);
  } else {
    for (const toast of state.toasts) {
      const removeDelay = toast.duration ?? TOAST_REMOVE_DELAY;
      addToRemoveQueue(toast.id, removeDelay);
    }
  }
};

const handleDismissToast = (state: ToastState, action: DismissToastAction): ToastState => {
  const { toastId } = action;

  // Clear dismiss timeout if it exists
  if (toastId && toastDismissTimeouts.has(toastId)) {
    clearTimeout(toastDismissTimeouts.get(toastId));
    toastDismissTimeouts.delete(toastId);
  }

  // Schedule removal
  scheduleRemoveToasts(state, toastId);

  return {
    ...state,
    toasts: state.toasts.map(t =>
      t.id === toastId || toastId === undefined
        ? {
            ...t,
            open: false,
          }
        : t
    ),
  };
};

const handleRemoveToast = (state: ToastState, action: RemoveToastAction): ToastState => {
  clearToastTimeouts(action.toastId);

  if (action.toastId === undefined) {
    return {
      ...state,
      toasts: [],
    };
  }
  return {
    ...state,
    toasts: state.toasts.filter(t => t.id !== action.toastId),
  };
};

export const reducer = (state: ToastState, action: ToastAction): ToastState => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return handleAddToast(state, action);
    case actionTypes.UPDATE_TOAST:
      return handleUpdateToast(state, action);
    case actionTypes.DISMISS_TOAST:
      return handleDismissToast(state, action);
    case actionTypes.REMOVE_TOAST:
      return handleRemoveToast(state, action);
  }
};

const listeners: ((state: ToastState) => void)[] = [];

let memoryState: ToastState = { toasts: [] };

function dispatch(action: ToastAction): void {
  memoryState = reducer(memoryState, action);
  for (const listener of listeners) {
    listener(memoryState);
  }
}

function toast(props: Omit<Toast, 'id' | 'open'>): {
  id: string;
  dismiss: () => void;
  update: (props: Partial<Toast>) => void;
} {
  const id = genId();

  const update = (updateProps: Partial<Toast>): void =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...updateProps, id },
    });

  const dismiss = (): void => dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id });

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
    },
  });

  return {
    id,
    dismiss,
    update,
  };
}

function useToast(): ToastState & {
  toast: typeof toast;
  dismiss: (toastId?: string) => void;
} {
  const [state, setState] = useState(memoryState);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: actionTypes.DISMISS_TOAST, ...(toastId && { toastId }) }),
  };
}

export { useToast, toast };
